import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { ArrowLeft, Copy, CheckCircle, QrCode, X } from 'lucide-react';
import Navigation from '@/components/Navigation';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CryptoReceivePage = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [wallets, setWallets] = useState([]);
  const [username, setUsername] = useState('');
  const [loading, setLoading] = useState(true);
  const [showQR, setShowQR] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState('');

  useEffect(() => {
    loadWalletData();
  }, []);

  const loadWalletData = async () => {
    try {
      const token = localStorage.getItem('routeflow_token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      // Load wallets
      const walletsRes = await axios.get(`${API}/crypto/wallet/${user.id}`, config);
      setWallets(walletsRes.data);

      // Try to get username
      try {
        const usernameRes = await axios.get(`${API}/crypto/username/lookup/${user.email}`, config);
        if (usernameRes.data?.username) {
          setUsername(usernameRes.data.username);
        }
      } catch (err) {
        console.log('No username set');
      }
    } catch (error) {
      console.error('Error loading wallet data:', error);
      toast.error('Failed to load wallet data');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`✅ ${label} copied to clipboard!`, {
      duration: 2000,
    });
  };

  const formatAddress = (address) => {
    return `${address.slice(0, 10)}...${address.slice(-8)}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
        <Navigation user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
      <Navigation user={user} onLogout={onLogout} />
      
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pt-24">
        <Button
          variant="ghost"
          onClick={() => navigate('/crypto/wallet')}
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Wallet
        </Button>

        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-2xl">Receive Crypto</CardTitle>
            <CardDescription>
              Share your username or wallet address to receive crypto from anyone
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Username Section */}
            {username ? (
              <div className="p-6 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl text-white">
                <div className="text-center">
                  <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-10 h-10" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Your RouteFlow Username</h3>
                  <div className="flex items-center justify-center space-x-3 mb-4">
                    <p className="text-4xl font-bold">@{username}</p>
                    <Button
                      onClick={() => copyToClipboard(`@${username}`, 'Username')}
                      className="bg-white/20 hover:bg-white/30 text-white"
                      size="sm"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-emerald-50 text-sm">
                    Anyone can send you crypto using this username!
                  </p>
                </div>
              </div>
            ) : (
              <div className="p-6 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="text-center">
                  <h3 className="font-semibold text-amber-900 mb-2">Setup Your Username</h3>
                  <p className="text-sm text-amber-700 mb-4">
                    Make it easy for others to send you crypto with a simple @username
                  </p>
                  <Button
                    onClick={() => navigate('/crypto/setup-username')}
                    className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
                  >
                    Setup Username Now
                  </Button>
                </div>
              </div>
            )}

            {/* Wallet Addresses */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Wallet Addresses</h3>
              <p className="text-sm text-gray-600 mb-4">
                You can also receive crypto directly to your wallet address
              </p>
              
              <div className="space-y-3">
                {wallets.map((wallet, index) => (
                  <div key={index} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-center justify-between mb-2">
                      <Badge variant="outline" className="capitalize">
                        {wallet.network.replace('_', ' ')}
                      </Badge>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => {
                            setSelectedAddress(wallet.address);
                            setShowQR(true);
                          }}
                          size="sm"
                          variant="outline"
                        >
                          <QrCode className="w-3 h-3 mr-1" />
                          QR Code
                        </Button>
                        <Button
                          onClick={() => copyToClipboard(wallet.address, 'Address')}
                          size="sm"
                          variant="outline"
                        >
                          <Copy className="w-3 h-3 mr-1" />
                          Copy
                        </Button>
                      </div>
                    </div>
                    <div className="bg-white p-3 rounded border border-gray-200">
                      <p className="text-sm font-mono text-gray-700 break-all">
                        {wallet.address}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Instructions */}
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="font-semibold text-blue-900 mb-2">💡 How to Receive</h4>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Share your @username with the sender</li>
                <li>• Or share your wallet address for direct transfers</li>
                <li>• Make sure the sender uses the correct network (Polygon recommended)</li>
                <li>• Your balance will update automatically once the transaction confirms</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Share Buttons */}
        <Card>
          <CardHeader>
            <CardTitle>Share Your Details</CardTitle>
            <CardDescription>Quick ways to share your receiving information</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {username && (
              <div className="flex items-center space-x-3">
                <Input
                  value={`Send me crypto on RouteFlow: @${username}`}
                  readOnly
                  className="flex-1"
                />
                <Button
                  onClick={() => copyToClipboard(`Send me crypto on RouteFlow: @${username}`, 'Share message')}
                  variant="outline"
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            )}
            
            {wallets.length > 0 && (
              <div className="flex items-center space-x-3">
                <Input
                  value={wallets[0].address}
                  readOnly
                  className="flex-1"
                />
                <Button
                  onClick={() => copyToClipboard(wallets[0].address, 'Wallet address')}
                  variant="outline"
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* QR Code Modal */}
        {showQR && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-md">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Scan to Send</CardTitle>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowQR(false)}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
                <CardDescription>
                  Share this QR code to receive crypto
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-center p-6 bg-white rounded-lg">
                  <QRCodeSVG
                    value={selectedAddress}
                    size={256}
                    level="H"
                    includeMargin={true}
                  />
                </div>
                <div className="text-center">
                  <p className="text-sm text-gray-600 mb-2">Wallet Address</p>
                  <p className="text-xs font-mono text-gray-700 break-all bg-gray-50 p-2 rounded">
                    {selectedAddress}
                  </p>
                </div>
                <Button
                  onClick={() => copyToClipboard(selectedAddress, 'Address')}
                  className="w-full"
                  variant="outline"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copy Address
                </Button>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default CryptoReceivePage;
